/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   Stores and retrieved requested tours by entity in local storage
 */

import { memoizeDeepReturnValue } from '@zcode/memoizers'
import { localStorageSingleton as LocalStorage } from '@zcode/nr-utils'
import type { Listable, MessageData, Pin, Property, RawPinInventorySearch } from '@zcode/state-type'
import { differenceInHours, parse } from 'date-fns'

export interface RequestedTour
  extends Pick<Listable | Property, 'buildingId' | 'listingId'>,
    Required<Pick<MessageData, 'selectedTimes' | 'selectedTimesISO8601'>> {
  hasInstantTourScheduling: boolean | undefined
  selectedTimesISO8601: readonly string[]
  floorplans?: readonly number[] | null
}

const getRequestedTourKey = (entity: Listable | Property | Pin<RawPinInventorySearch>) =>
  `tours.${entity.groupId}` as const

export const getRequestedTour = memoizeDeepReturnValue(
  (entity: Listable | Property | Pin<RawPinInventorySearch>): RequestedTour | null => {
    const tourRequested = LocalStorage.getx(getRequestedTourKey(entity))
    if (entity.isFloorplan) {
      // in order to disable tour request buttons for floor plans we need to check if the tour was requested
      // for a specific floor plan in the building
      return tourRequested?.floorplans?.includes(entity.listingId) ? tourRequested : undefined
    }
    return tourRequested
  }
)

export const setRequestedTour = (
  entity: Listable | Property,
  hasInstantTourScheduling: boolean,
  {
    selectedTimes,
    selectedTimesISO8601,
  }: Pick<MessageData, 'selectedTimes' | 'selectedTimesISO8601'>,
  floorplanId?: number | null
) => {
  if (selectedTimes?.length && selectedTimesISO8601?.length) {
    const prevRequestedTour = getRequestedTour(entity)
    const requestedTour: RequestedTour = prevRequestedTour ?? {
      buildingId: entity.buildingId,
      listingId: entity.listingId,
      hasInstantTourScheduling,
      selectedTimesISO8601,
      selectedTimes,
    }
    if (entity.isBuilding && floorplanId) {
      requestedTour.floorplans = requestedTour.floorplans
        ? [...requestedTour.floorplans, floorplanId]
        : [floorplanId]
    }
    const now = new Date()
    const ttlInDays = Math.max(
      ...selectedTimesISO8601.map((time) =>
        Math.ceil(differenceInHours(parse(time, "yyyy-MM-dd'T'kk:mm':00.000", now), now) / 24)
      )
    )
    LocalStorage.setx(getRequestedTourKey(entity), requestedTour, ttlInDays)
  }
}
